import { html, css } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { BaseComponent } from "../base-component/base-component";
import emit from "../../common/events";

declare global {
  interface Window {
    YT: any;
  }

  interface HTMLElementTagNameMap {
    'lite-youtube': YoutubeLite;
  }
}

/**
 * A lightweight YouTube embed. Displays a poster image and play button, then loads the real
 * iframe on user interaction. This provides the same user experience as a standard YouTube embed
 * but is MUCH faster to initialize and paint.
 *
 * Based on https://github.com/nickersoft/lite-youtube-embed
 *
 * @element lite-youtube
 *
 * @prop {string} videoid - The YouTube video ID.
 * @prop {string} playlabel - Accessible label for the play button.
 * @prop {string} params - Additional YouTube player URL parameters (e.g. "start=30&end=60").
 * @prop {number} width - Width of the iframe player.
 * @prop {number} height - Height of the iframe player.
 * @prop {string} autoplay - Whether to autoplay when activated ("1" or "0").
 *
 * @fires video-play - When the YouTube video starts playing.
 * @fires video-pause - When the YouTube video is paused.
 * @fires video-ended - When the YouTube video finishes playing.
 */
@customElement('lite-youtube')
export class YoutubeLite extends BaseComponent {
  static styles = [css`
    :host {
      background-color: #000;
      position: relative;
      display: block;
      contain: content;
      background-position: center center;
      background-size: cover;
      cursor: pointer;
    }

    :host::after {
      content: "";
      display: block;
      padding-bottom: calc(100% / (16 / 9));
    }

    iframe {
      width: 100%;
      height: 100%;
      position: absolute;
      top: 0;
      left: 0;
      border: 0;
    }

    .lty-playbtn {
      width: 68px;
      height: 48px;
      position: absolute;
      cursor: pointer;
      transform: translate3d(-50%, -50%, 0);
      top: 50%;
      left: 50%;
      z-index: 1;
      background-color: transparent;
      background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" version="1.1" viewBox="0 0 68 48"><path fill="%23f00" fill-opacity="0.8" d="M66.52,7.74c-0.78-2.93-2.49-5.41-5.42-6.19C55.79,.13,34,0,34,0S12.21,.13,6.9,1.55 C3.97,2.33,2.27,4.81,1.48,7.74C0.06,13.05,0,24,0,24s0.06,10.95,1.48,16.26c0.78,2.93,2.49,5.41,5.42,6.19 C12.21,47.87,34,48,34,48s21.79-0.13,27.1-1.55c2.93-0.78,4.64-3.26,5.42-6.19C67.94,34.95,68,24,68,24S67.94,13.05,66.52,7.74z"></path><path d="M 45,24 27,14 27,34" fill="%23fff"></path></svg>');
      filter: grayscale(100%);
      transition: filter .1s cubic-bezier(0, 0, 0.2, 1);
      border: none;
    }

    :host(:hover) .lty-playbtn,
    .lty-playbtn:focus {
      filter: none;
    }

    :host(.lyt-activated) {
      cursor: unset;
    }

    :host(.lyt-activated)::before,
    :host(.lyt-activated) .lty-playbtn {
      opacity: 0;
      pointer-events: none;
    }

    .lyt-visually-hidden {
      clip: rect(0 0 0 0);
      clip-path: inset(50%);
      height: 1px;
      overflow: hidden;
      position: absolute;
      white-space: nowrap;
      width: 1px;
    }
  `];

  /**
   * The YouTube video ID.
   */
  @property()
  videoid = '';

  /**
   * Accessible label for the play button.
   */
  @property()
  playlabel = 'Play';

  /**
   * Additional YouTube player URL parameters.
   */
  @property()
  params = '';

  /**
   * Width of the iframe player.
   */
  @property({ type: Number })
  width = 560;

  /**
   * Height of the iframe player.
   */
  @property({ type: Number })
  height = 315;

  /**
   * Whether to autoplay when activated.
   */
  @property()
  autoplay = '1';

  /**
   * Whether the player has been activated (iframe loaded).
   */
  @state()
  private activated = false;

  /**
   * Reference to the YouTube Player API instance.
   */
  private player: any = null;

  /**
   * Promise that resolves when the YT API is ready.
   */
  private ytApiPromise: Promise<void> | null = null;

  /**
   * Whether preconnect links have already been added.
   */
  private static preconnected = false;

  /**
   * Whether the browser needs the YT API for autoplay (Safari + mobile).
   */
  private needsYTApiForAutoplay = false;

  connectedCallback() {
    super.connectedCallback();

    this.needsYTApiForAutoplay =
      navigator.vendor.includes('Apple') || navigator.userAgent.includes('Mobi');

    this.loadPosterImage();

    // Warm connections on hover
    this.addEventListener('pointerover', YoutubeLite.warmConnections, { once: true });
  }

  /**
   * Resolves the best available poster image (WebP → JPEG maxres → JPEG sd → JPEG hq)
   * and sets it as the host element's background.
   */
  private loadPosterImage() {
    if (this.style.backgroundImage) return;

    const jpgUrl  = (q: string) => `https://i.ytimg.com/vi/${this.videoid}/${q}.jpg`;
    const webpUrl = `https://i.ytimg.com/vi_webp/${this.videoid}/maxresdefault.webp`;

    const probe = new Image();
    probe.src = webpUrl;
    probe.onload = () => {
      if (probe.naturalWidth > 120) {
        this.style.backgroundImage = `url("${webpUrl}")`;
      } else {
        const jpgProbe = new Image();
        jpgProbe.src = jpgUrl('maxresdefault');
        jpgProbe.onload = () => {
          this.style.backgroundImage = jpgProbe.naturalWidth > 120
            ? `url("${jpgUrl('maxresdefault')}")` 
            : `url("${jpgUrl('sddefault')}")` ;
        };
        jpgProbe.onerror = () => {
          this.style.backgroundImage = `url("${jpgUrl('hqdefault')}")`;
        };
      }
    };
    probe.onerror = () => {
      this.style.backgroundImage = `url("${jpgUrl('hqdefault')}")`;
    };
  }

  /**
   * Add a `<link rel={preload|preconnect}>` to the head.
   */
  private static addPrefetch(kind: string, url: string, as?: string) {
    const linkEl = document.createElement('link');
    linkEl.rel = kind;
    linkEl.href = url;
    if (as) {
      linkEl.as = as;
    }
    document.head.append(linkEl);
  }

  /**
   * Begin pre-connecting to warm up the iframe load.
   * Since the embed's network requests load within its iframe,
   * preload/prefetch'ing them outside the iframe will only cause double-downloads.
   * So, the best we can do is warm up a few connections to origins in the critical path.
   */
  private static warmConnections() {
    if (YoutubeLite.preconnected) {
      return;
    }

    YoutubeLite.addPrefetch('preconnect', 'https://www.youtube-nocookie.com');
    YoutubeLite.addPrefetch('preconnect', 'https://www.google.com');
    YoutubeLite.addPrefetch('preconnect', 'https://googleads.g.doubleclick.net');
    YoutubeLite.addPrefetch('preconnect', 'https://static.doubleclick.net');

    YoutubeLite.preconnected = true;
  }

  /**
   * Loads the YouTube IFrame Player API script if not already loaded.
   */
  private fetchYTPlayerApi() {
    if (window.YT?.Player) {
      return;
    }

    this.ytApiPromise = new Promise((resolve, reject) => {
      const el = document.createElement('script');
      el.src = 'https://www.youtube.com/iframe_api';
      el.async = true;
      el.onload = () => {
        window.YT.ready(resolve);
      };
      el.onerror = reject;
      document.head.append(el);
    });
  }

  /**
   * Creates a player via the YT Player API (used on Safari / mobile).
   */
  private async addYTPlayerIframe(params: URLSearchParams) {
    this.fetchYTPlayerApi();
    await this.ytApiPromise;

    const container = this.renderRoot.querySelector('#yt-player-container');
    if (!container) return;

    const videoPlaceholderEl = document.createElement('div');
    container.append(videoPlaceholderEl);

    const paramsObj = Object.fromEntries(params.entries());

    this.player = new window.YT.Player(videoPlaceholderEl, {
      width: '100%',
      videoId: this.videoid,
      playerVars: paramsObj,
      events: {
        onReady: (event: any) => {
          event.target.playVideo();
        },
        onStateChange: this.onPlayerStateChange.bind(this),
      },
    });
  }

  /**
   * Handles click — activates the player by loading the real YouTube iframe.
   */
  private async activate() {
    if (this.activated) {
      return;
    }
    this.activated = true;
    this.classList.add('lyt-activated');

    const params = new URLSearchParams(this.params || []);
    params.append('autoplay', this.autoplay);
    params.append('playsinline', '1');
    params.append('enablejsapi', '1');
    params.append('modestbranding', '1');
    params.append('html5', '1');
    params.append('controls', '1');

    if (this.needsYTApiForAutoplay) {
      return this.addYTPlayerIframe(params);
    }

    // Wait for the Lit update to render the #yt-player-container
    await this.updateComplete;

    const container = this.renderRoot.querySelector('#yt-player-container');
    if (!container) return;

    const iframeEl = document.createElement('iframe');
    const iframeId = `mh-player-youtube-${this.videoid}`;
    iframeEl.id = iframeId;
    iframeEl.width = String(this.width);
    iframeEl.height = String(this.height);
    iframeEl.title = this.playlabel;
    iframeEl.allow = 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture';
    iframeEl.allowFullscreen = true;
    iframeEl.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(this.videoid)}?${params.toString()}`;
    container.append(iframeEl);

    this.fetchYTPlayerApi();
    await this.ytApiPromise;

    this.player = new window.YT.Player(iframeEl, {
      events: {
        onStateChange: this.onPlayerStateChange.bind(this),
      },
    });

    // Set focus for a11y
    iframeEl.focus();
  }

  /**
   * Pauses the YouTube video.
   */
  public pause() {
    this.player?.pauseVideo();
  }

  /**
   * Plays the YouTube video.
   */
  public play() {
    this.player?.playVideo();
  }

  public addIframe() {
    return this.activate();
  }

  /**
   * Gathers common analytics data from the host element's data-layer attributes
   * and DOM context (modal, collapse, area, etc.). Mirrors the original
   * `getCommonAnalytics(element.parentElement)` behavior from lite-youtube-embed.js.
   */
  private getCommonAnalytics(): Record<string, any> {
    const data: Record<string, any> = {};
    const element = this as HTMLElement;

    // Gather all data-layer* attributes from the host element
    Object.keys(element.dataset).forEach((key) => {
      if (element.dataset[key] && key.startsWith('layer')) {
        data[this.formatPropertyName(key)] = element.dataset[key];
      }
    });

    data.id = data.id || element.closest('[id]')?.id;
    data.label = data.label || element.innerText || element.ariaLabel;

    if (element.dataset.layerSubarea === 'modal') {
      data.modalId = element.closest('.modal')?.id ?? '';
    }

    const modalContentElement = element.closest('.modal');
    if (modalContentElement) {
      data.modalId = modalContentElement.id;
      data.regionId = modalContentElement.getAttribute('data-layer-region-id');
      data.regionName = modalContentElement.getAttribute('data-layer-region-label');
    }

    if (element.dataset.layerSubarea === 'collapse') {
      data.collapseId = element.closest('.collapse')?.id ?? '';
    }

    const areaElement = element.closest('[data-area-name]') as HTMLElement | null;
    if (areaElement) {
      data.subarea = areaElement.dataset.areaName;
    }

    if ((element as any).href) {
      data.href = (element as any).href;
    }

    // Reverse logic is intentional — at this moment BS javascript has not flipped the property yet.
    if (element.ariaExpanded) {
      data.expanded = element.ariaExpanded === 'true' ? 'false' : 'true';
    }

    if (element.ariaSelected) {
      data.selected = element.ariaSelected === 'true' ? 'false' : 'true';
    }

    return data;
  }

  private formatPropertyName(name: string): string {
    return this.lowerFirstLetter(name.replace(/^layer\.*/, ''));
  }

  private lowerFirstLetter(str: string): string {
    return str.charAt(0).toLowerCase() + str.slice(1);
  }

  /**
   * Handles YT Player state change events and emits analytics.
   */
  private onPlayerStateChange(event: any) {
    const currentPlayer = event.target;
    const element = currentPlayer?.getIframe?.();
    if (!currentPlayer || !element) return;

    const currentTime = currentPlayer.playerInfo?.currentTime ?? 0;
    const duration = currentPlayer.playerInfo?.duration ?? 1;
    const videoId = currentPlayer.playerInfo?.videoData?.video_id ?? this.videoid;
    const videoName = currentPlayer.videoTitle ?? '';
    const commonData = this.getCommonAnalytics();

    switch (event.data) {
      case window.YT.PlayerState.PLAYING:
        emit({
          element: this,
          name: 'video-play',
          detail: {
            ...commonData,
            'video-type': 'YouTube',
            'video-id': videoId,
            'video-name': videoName,
            'video-start-time': Math.ceil(currentTime),
          },
        });
        break;

      case window.YT.PlayerState.PAUSED: {
        const completePercentage = Math.floor((currentTime / duration) * 100);
        emit({
          element: this,
          name: 'video-pause',
          detail: {
            ...commonData,
            'video-type': 'Youtube',
            'video-id': videoId,
            'video-name': videoName,
            'video-pause-at-time': Math.ceil(currentTime),
            'video-complete-percentage': completePercentage,
          },
        });
        break;
      }

      case window.YT.PlayerState.ENDED:
        emit({
          element: this,
          name: 'video-ended',
          detail: {
            ...commonData,
            'video-type': 'Youtube',
            'video-id': videoId,
            'video-name': videoName,
            'video-complete-percentage': 100,
          },
        });
        break;
    }
  }

  renderComp() {
    if (this.activated) {
      return html`<div id="yt-player-container"></div>`;
    }

    return html`
      <button
        type="button"
        class="lty-playbtn"
        @click=${this.activate}
      >
        <span class="lyt-visually-hidden">${this.playlabel}</span>
      </button>
    `;
  }
}
